import type { MenuProps } from '@mui/material';
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';

type ContextMenuAnchorEl = MenuProps['anchorEl'];

export type ContextMenuState = {
  open: boolean;
  anchorEl: ContextMenuAnchorEl;
  itemID: string | null;
  data?: Record<string, unknown>;
};

interface ContextMenuType {
  contextMenu: ContextMenuState;
  openContextMenu: (
    itemID: string,
    anchorEl: ContextMenuAnchorEl,
    data?: Record<string, unknown>,
  ) => void;
  closeContextMenu: () => void;
}

interface ContextMenuProviderProps {
  children: ReactNode;
}

const ContextMenu = createContext<ContextMenuType | undefined>(undefined);

export const useContextMenu = (): ContextMenuType => {
  const context = useContext(ContextMenu);
  if (context === undefined) {
    throw new Error('useContextMenu must be used within a ContextMenuProvider');
  }

  return context;
};

export const ContextMenuProvider = ({ children }: ContextMenuProviderProps) => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    open: false,
    anchorEl: null,
    itemID: null,
    data: {},
  });

  const openContextMenu = (
    itemID: string,
    anchorEl: ContextMenuAnchorEl,
    data: Record<string, unknown> = {},
  ) => {
    setContextMenu({
      open: true,
      anchorEl,
      itemID,
      data,
    });
  };

  const closeContextMenu = () => {
    setContextMenu((prevState) => ({ ...prevState, open: false, isDeletable: false }));
  };

  return (
    <ContextMenu.Provider value={{ contextMenu, openContextMenu, closeContextMenu }}>
      {children}
    </ContextMenu.Provider>
  );
};
